import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiHeart, FiMenu, FiShoppingCart, FiUser, FiX } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import SearchBox from './SearchBox';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const { getCartItemsCount } = useCart();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      setIsUserMenuOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  return (
    <>
      <div className="bg-[#1A1A1A] text-white">
        <div className="sc-container flex items-center justify-center gap-2 py-2 text-xs font-semibold">
          <span className="rounded-full bg-[#DB4444] px-2 py-0.5 text-[10px] uppercase tracking-wider">
            Live
          </span>
          <span>Weekend campaign: up to 50% off selected products + express delivery.</span>
        </div>
      </div>

      <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/90 backdrop-blur-xl">
        <div className="sc-container">
          <div className="flex h-20 items-center justify-between gap-4">
            <Link to="/" className="group flex items-center gap-2">
              <img src="/logo.png" alt="ShopCircuit" className="sc-hover-smooth h-12 w-auto object-contain group-hover:scale-105" />
              <span className="hidden text-lg font-bold tracking-tight text-[#1A1A1A] sm:inline-block">
                ShopCircuit
              </span>
            </Link>

            <nav className="hidden items-center gap-6 text-sm font-semibold text-slate-600 lg:flex">
              <Link to="/" className="sc-hover-smooth hover:text-[#DB4444]">
                Home
              </Link>
              <Link to="/products" className="sc-hover-smooth hover:text-[#DB4444]">
                Shop
              </Link>
              <Link to="/about" className="sc-hover-smooth hover:text-[#DB4444]">
                About
              </Link>
              <Link to="/contact" className="sc-hover-smooth hover:text-[#DB4444]">
                Contact
              </Link>
            </nav>

            <SearchBox className="hidden max-w-xl flex-1 md:block" />

            <div className="hidden items-center gap-1 md:flex">
              <Link
                to="/wishlist"
                className="sc-hover-smooth rounded-full p-2.5 text-slate-600 hover:bg-slate-100 hover:text-[#DB4444]"
                aria-label="Wishlist"
              >
                <FiHeart size={18} />
              </Link>

              <Link
                to="/cart"
                className="sc-hover-smooth relative rounded-full p-2.5 text-slate-600 hover:bg-slate-100 hover:text-[#DB4444]"
                aria-label="Cart"
              >
                <FiShoppingCart size={18} />
                {getCartItemsCount() > 0 && (
                  <span className="absolute right-1 top-1 flex h-5 min-w-[18px] items-center justify-center rounded-full bg-[#DB4444] px-1 text-[10px] font-bold text-white">
                    {getCartItemsCount()}
                  </span>
                )}
              </Link>

              {user ? (
                <div className="relative">
                  <button
                    onClick={() => setIsUserMenuOpen((prev) => !prev)}
                    className="sc-hover-smooth rounded-full p-2.5 text-slate-600 hover:bg-slate-100 hover:text-[#DB4444]"
                    aria-label="User menu"
                  >
                    <FiUser size={18} />
                  </button>
                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-2 w-56 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
                      <Link
                        to="/profile"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="sc-hover-smooth block px-4 py-2.5 text-sm hover:bg-slate-50"
                      >
                        Manage My Account
                      </Link>
                      <Link
                        to="/orders"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="sc-hover-smooth block px-4 py-2.5 text-sm hover:bg-slate-50"
                      >
                        My Orders
                      </Link>
                      <Link
                        to="/wishlist"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="sc-hover-smooth block px-4 py-2.5 text-sm hover:bg-slate-50"
                      >
                        My Wishlist
                      </Link>
                      {user.isAdmin && (
                        <Link
                          to="/admin"
                          onClick={() => setIsUserMenuOpen(false)}
                          className="sc-hover-smooth block px-4 py-2.5 text-sm font-semibold text-[#DB4444] hover:bg-red-50"
                        >
                          Admin Panel
                        </Link>
                      )}
                      <button
                        onClick={handleLogout}
                        className="sc-hover-smooth block w-full border-t border-slate-100 px-4 py-2.5 text-left text-sm font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/login"
                  className="sc-hover-smooth rounded-full p-2.5 text-slate-600 hover:bg-slate-100 hover:text-[#DB4444]"
                  aria-label="Login"
                >
                  <FiUser size={18} />
                </Link>
              )}
            </div>

            <button
              className="sc-hover-smooth rounded-full p-2.5 text-slate-700 hover:bg-slate-100 md:hidden"
              onClick={() => setIsMenuOpen((prev) => !prev)}
              aria-label="Toggle mobile menu"
            >
              {isMenuOpen ? <FiX size={20} /> : <FiMenu size={20} />}
            </button>
          </div>

          <div className="pb-4 md:hidden">
            <SearchBox />
          </div>
        </div>

        {isMenuOpen && (
          <div className="border-t border-slate-200 bg-white md:hidden">
            <nav className="sc-container flex flex-col gap-1 py-3">
              <Link to="/" onClick={() => setIsMenuOpen(false)} className="sc-hover-smooth rounded-lg px-3 py-2 text-sm font-semibold hover:bg-slate-50">
                Home
              </Link>
              <Link
                to="/products"
                onClick={() => setIsMenuOpen(false)}
                className="sc-hover-smooth rounded-lg px-3 py-2 text-sm font-semibold hover:bg-slate-50"
              >
                Shop
              </Link>
              <Link
                to="/wishlist"
                onClick={() => setIsMenuOpen(false)}
                className="sc-hover-smooth rounded-lg px-3 py-2 text-sm font-semibold hover:bg-slate-50"
              >
                Wishlist
              </Link>
              <Link
                to="/cart"
                onClick={() => setIsMenuOpen(false)}
                className="sc-hover-smooth rounded-lg px-3 py-2 text-sm font-semibold hover:bg-slate-50"
              >
                Cart
              </Link>
              {user ? (
                <>
                  <Link
                    to="/orders"
                    onClick={() => setIsMenuOpen(false)}
                    className="sc-hover-smooth rounded-lg px-3 py-2 text-sm font-semibold hover:bg-slate-50"
                  >
                    Orders
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    className="sc-hover-smooth rounded-lg px-3 py-2 text-left text-sm font-semibold text-[#DB4444] hover:bg-red-50"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setIsMenuOpen(false)}
                  className="sc-hover-smooth rounded-lg px-3 py-2 text-sm font-semibold text-[#DB4444] hover:bg-red-50"
                >
                  Login
                </Link>
              )}
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
